import { useState, useEffect, useMemo, useCallback, useRef } from 'preact/hooks'
import { useOmneoClient } from '../hooks/useOmneoClient'
import { useSearchQuery } from '../hooks/useSearchQuery'
import { useToggle } from '../hooks/useToggle'
import { useFormInput } from '../hooks/useFormInput'
import { PageModal } from './AccountModal'
import Icon from '@/components/snippets/icon/icon'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'
import { getAllWishlists, removeVariantFromAllWishlists } from '../helpers/getAllWishlists'
import { accountTranslations } from '../helpers/translations'

/**
 * Import types from our type definition file
 * @typedef {import('../../../types/omneo-wishlist').ShopifyVariant} ShopifyVariant
 * @typedef {import('../../../types/omneo-wishlist').WishlistConfig} WishlistConfig
 * @typedef {import('../../../types/omneo-wishlist').List} List
 * @typedef {import('../../../types/omneo-wishlist').ListItem} ListItem
 * @typedef {import('../../../types/omneo-wishlist').SectionData} SectionData
 * @typedef {import('../../../types/omneo-wishlist').OmneoWishlistProps} OmneoWishlistProps
 */

export const GALLERY_LIMIT = 4

export const imageGallery = (listItems, limit) => {
  return Array.from(new Array(limit)).map((_, i) => {
    const item = listItems[i]
    return item ? { img: item.meta.img } : { img: undefined }
  })
}

const parseJSONProps = (jsonString) => {
  const parseString = jsonString.replaceAll("'", '"').replaceAll('\n', '').trim()
  return JSON.parse(parseString)
}

const getVariantById = (id, variantData) =>
  variantData.find((variant) => variant.id === id)

const flatItemWishList = (lists) =>
  lists.reduce((acc, list) => acc.concat(list.items), [])

export const createProfileClientProxy = async (client, profileId) => {
  const omneoGlobalClient = client.client
  await omneoGlobalClient.profile.Connection(Number(profileId))

  return {
    updateWishlistItem: (listId, listItemId, body) =>
      client.updateWishlistItem(listId, listItemId, body),
    addWishlistItem: (listId, itemSku, meta) =>
      client.addWishlistItem(listId, itemSku, meta),
    removeWishlistItem: (listId, itemId) =>
      client.removeWishlistItem(listId, itemId)
  }
}

const WishlistIcon = () => (
  <div className="wkh-icon" icon="wishlist">
    <svg viewBox="0 0 64 64">
      <path
        className="fill-transparent group-data-visible/wishlist:fill-black"
        d="M32.012,59.616c-1.119-.521-2.365-1.141-3.707-1.859a79.264,79.264,0,0,1-11.694-7.614C6.316,42,.266,32.6.254,22.076,0.244,12.358,7.871,4.506,17.232,4.5a16.661,16.661,0,0,1,11.891,4.99l2.837,2.889,2.827-2.9a16.639,16.639,0,0,1,11.874-5.02h0c9.368-.01,17.008,7.815,17.021,17.539,0.015,10.533-6.022,19.96-16.312,28.128a79.314,79.314,0,0,1-11.661,7.63C34.369,58.472,33.127,59.094,32.012,59.616Z"
      ></path>
    </svg>
  </div>
)

/**
 * Checkbox component
 */
export const Checkbox = ({ isChecked, name, value, handleIsChecked, disabled }) => (
  <div onClick={handleIsChecked} className={`flex h-6 w-6 border ${isChecked ? 'bg-black' : ''}`}>
    <button
      type="button"
      className="flex items-center justify-center w-full h-full m-auto"
      disabled={disabled}
    >
      {isChecked && <Icon id="tick" className="text-white" />}
    </button>
    <input name={name} value={isChecked} type="hidden" />
  </div>
)

/**
 * Wishlist row component
 * @param {Object} props - Component props
 * @param {List} props.list - The wishlist
 * @param {string} props.variantId - The variant ID
 * @param {boolean} props.isLoading - Loading state
 * @param {boolean} props.isNewList - Whether this is a new list
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.isCreateList - Whether we're creating a list
 * @param {Function} props.handleChange - Change handler
 */
export const WishListRow = ({
  list,
  variantId,
  isLoading,
  isNewList,
  children,
  isCreateList,
  handleChange
}) => {
  const [isChecked, setIsChecked] = useState(() =>
    !!list?.items.find(item => String(item.meta.variantId) === String(variantId)) ?? false
  )

  const handleIsChecked = () => setIsChecked(prev => !prev)
  const images = useMemo(
    () => imageGallery(list?.items ?? [], GALLERY_LIMIT).filter(item => item.img),
    [list?.items]
  )

  useEffect(() => {
    handleChange?.()
  }, [isChecked, handleChange])

  return (
    <div
      className={`flex flex-col gap-2 border-b border-neutrals-light-grey pb-6 ${isNewList ? 'new-list-container' : ''}`}
    >
      <div className="flex flex-row items-center gap-2">
        {!isNewList ? (
          <>
            <input type="hidden" name={`profile_${list.id}`} value={list.profile?.id} />
            <Checkbox
              name={`wishlist_${list.id}`}
              value={list.id}
              isChecked={isChecked && !isCreateList}
              handleIsChecked={handleIsChecked}
              disabled={isLoading || isCreateList}
            />
          </>
        ) : (
          <Checkbox name="new-list" isChecked={true} disabled={true} />
        )}
        <div className="flex items-center w-full gap-2">
          {!!list?.profile?.connector?.avatar_url && (
            <img
              className="h-5.5 w-5.5 rounded-full"
              src={list.profile?.connector?.avatar_url}
              alt="Profile avatar"
            />
          )}
          {isNewList ? children : <div>{list.name}</div>}
        </div>
      </div>
      <div className="flex flex-row gap-2">
        {images.length > 0 ? (
          images.map(({ img }, index) => (
            <div key={index} className="relative flex-1 overflow-hidden rounded max-w-1/4 aspect-square">
              <img src={img} className="object-cover object-bottom w-full h-full" alt="Wishlist item" />
              {list?.items.length > 4 && index === 3 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <div className="font-bold text-white paragraph-lg">+{list.items.length - 3}</div>
                </div>
              )}
          </div>
          ))) : (
            <div className="py-8">{accountTranslations.wishlist.no_items}</div>
        )}
      </div>
    </div>
  )
}

/**
 * New wishlist form component
 */
const NewWishlist = ({ handleCreateList, isLoading }) => {
  const newListRef = useRef(null)
  const { formInputs, handleOnChange } = useFormInput({
    wishlist_name: ''
  })

  useEffect(() => {
    if (newListRef.current) {
      newListRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  return (
    <WishListRow isNewList={true} isCreateList={true}>
      <div ref={newListRef} className="flex items-center w-full">
        <div className="flex-1">
          <input
            name={formInputs.wishlist_name.key}
            value={formInputs.wishlist_name.value}
            onChange={handleOnChange}
            className="w-full !border-none !outline-none !ring-0"
            placeholder={accountTranslations.wishlist.name_your_list}
            disabled={isLoading}
          />
        </div>
        {!isLoading && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCreateList}
              disabled={isLoading}
            >
              <Icon id="close" className="text-primary-black" />
            </button>
            <button type="submit" disabled={isLoading}>
              <Icon id="tick" className="text-primary-black" />
            </button>
          </div>
        )}
      </div>
    </WishListRow>
  )
}

const WishlistModal = ({
  children,
  onSubmit,
  isCreateList,
  handleCreateList,
  isLoading,
  hasChange,
  handleChangeSize,
  handleSelectSize,
  formRef,
  sizeButtonState,
  paramValue
}) => (
  <form ref={formRef} onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
    <div className="flex flex-col flex-1 gap-4 p-6 overflow-y-auto">{children}</div>
    <div className="flex flex-row flex-wrap gap-2 p-6 bg-neutrals-base">
      <button
        className="relative overflow-hidden grow basis-1/2 btn btn-md btn-outline-black"

        type="button"
        disabled={isLoading || isCreateList}
        onClick={handleCreateList}
      >
        {isLoading && isCreateList ? (
          <LoaderSpin wrapperClass="bg-black" />
        ) : (
          accountTranslations.wishlist.create_wishlist
        )}
      </button>
      {!isCreateList && paramValue && (
        <button
          className="relative overflow-hidden grow basis-1/2 btn btn-md btn-outline-black"
          type="button"
          onClick={() => sizeButtonState === 'change' ? handleChangeSize() : handleSelectSize()}
      >
          {sizeButtonState === 'change' ? accountTranslations.wishlist.change_size : accountTranslations.wishlist.select_size}
        </button>
      )}
      <button
        className="relative overflow-hidden grow basis-1/2 btn btn-md btn-solid-black"
        type="submit"
        disabled={isLoading || isCreateList || !hasChange}
      >
        {isLoading && !isCreateList ? (
          <LoaderSpin wrapperClass="bg-black" />
        ) : (
          accountTranslations.wishlist.add_to_wishlist
        )}
      </button>

    </div>
  </form>
)

const useWishlistFormSubmit = ({
  client,
  config,
  isCreateList,
  setIsCreateList,
  paramValue,
  variantData,
  sectionData,
  setIsSelected,
  handleSectionData,
  handleOnLoadAsyncCallback,
  setIsLoading
}) => {
  return async (e) => {
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()
    setIsLoading(true)

    try {
      const formData = new FormData(e.currentTarget)
      const currentSelectedVariantId = paramValue

      const { createdList, selectedLists } = await processFormData(formData, isCreateList, client)

      if (!validateVariantData(variantData)) {
        setIsLoading(false)
        return
      }

      const { sku, image, id: variantIdFromData } = variantData

      if (variantIdFromData !== currentSelectedVariantId) {
        console.warn('Mismatch between variantData and current selection.')
      }

      if (selectedLists.some(sl => sl.selected)) {
        setIsSelected(true)
        handleSectionData('allListItems', {
          ...sectionData.allListItems,
          [currentSelectedVariantId]: 1
        })
      }

      await updateWishlists({
        selectedLists,
        isCreateList,
        createdList,
        sectionData,
        currentSelectedVariantId,
        client,
        sku,
        image,
        config
      })

      if (isCreateList) {
        setIsCreateList(false)
      }
      await handleOnLoadAsyncCallback(true)
    } catch (error) {
      console.error('Error submitting wishlist form:', error)
    } finally {
      setIsLoading(false)
    }
  }
}

const processFormData = async (formData, isCreateList, client) => {
  let createdList
  const selectedLists = []

  if (isCreateList) {
    const name = formData.get('wishlist_name')
    if (!name || String(name).trim() === '') {
      throw new Error('Wishlist name is required for new list.')
    }
    createdList = await client.profileClient.createWishlist(name)
    selectedLists.push({ id: createdList.id, selected: true, profileId: null })
  } else {
    formData.forEach((value, key) => {
      if (key.startsWith('wishlist_')) {
        const listId = key.split('_')[1]
        const existing = selectedLists.find((list) => list.id === listId)
        if (existing) {
          existing.selected = value === 'true'
        } else {
          selectedLists.push({ id: listId, selected: value === 'true', profileId: null })
        }
      } else if (key.startsWith('profile_')) {
        const listId = key.split('_')[1]
        const profileId = value
        const existing = selectedLists.find((list) => list.id === listId)
        if (existing) {
          existing.profileId = profileId
        } else {
          selectedLists.push({ id: listId, selected: false, profileId })
        }
      }
    })
  }

  return { createdList, selectedLists }
}

const validateVariantData = (variantData) => {
  if (!variantData || !variantData.id || !variantData.sku || !variantData.image) {
    console.error('Variant data is incomplete or missing.', variantData)
    return false
  }
  return true
}

const updateWishlists = async ({
  selectedLists,
  isCreateList,
  createdList,
  sectionData,
  currentSelectedVariantId,
  client,
  sku,
  image,
  config
}) => {
  for (const selectedList of selectedLists) {
    if (!selectedList.selected && !isCreateList) continue

    const listTarget = isCreateList
      ? createdList
      : sectionData.listData.find((l) => l.id.toString() === selectedList.id)

    if (!listTarget) {
      console.warn('Target list not found for ID:', selectedList.id)
      continue
    }

    let profileToUse = client.profileClient
    if (selectedList.profileId && selectedList.profileId !== client.profileClient?.profile?.id?.toString()) {
      profileToUse = await createProfileClientProxy(client, selectedList.profileId)
    }

    const itemInList = listTarget.items?.find(
      (item) => String(item.meta.variantId) === String(currentSelectedVariantId)
    )

    if (!itemInList && (selectedList.selected || isCreateList)) {
      const newItemMeta = {
        img: image,
        selected: sku,
        productId: config.product,
        variantId: currentSelectedVariantId
      }
      await profileToUse.addWishlistItem(listTarget.id, sku, newItemMeta)
    } else if (itemInList && !selectedList.selected && !isCreateList) {
      await profileToUse.removeWishlistItem(listTarget.id, itemInList.id)
    }

    if (selectedList.profileId && selectedList.profileId !== client.profileClient?.profile?.id?.toString()) {
      client.client.profile.Connection()
    }
  }
}

/**
 * Base component that handles the wishlist functionality
 * @param {Object} props - Component props
 * @param {import('@omneo/omneo-sdk').OmneoClient} props.client - Omneo client
 * @param {WishlistConfig} props.config - Wishlist configuration
 */
const AccountWishListPDPBase = ({ client, config }) => {
  const [searchParamVariant] = useSearchQuery('variant')
  const { isActive, handleToggle } = useToggle(false)
  const [hasChange, setHasChange] = useState(false)
  const [sectionData, setSectionData] = useState({
    variant: null,
    listData: [],
    isSelected: false,
    allListItems: {}
  })
  const [isSelected, setIsSelected] = useState(false)
  const [variantData, setVariantData] = useState(getVariantById(searchParamVariant, config.variants) || undefined)
  const [paramValue, setParamValue] = useState(searchParamVariant)
  const [isCreateList, setIsCreateList] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const formRef = useRef(null)
  const [shouldShowProductSelector, setShouldShowProductSelector] = useState(!paramValue)
  const [sizeButtonState, setSizeButtonState] = useState(paramValue ? 'change' : 'select')

  useEffect(() => {
    if (searchParamVariant) {
      setParamValue(searchParamVariant)
      if (variantData?.id !== searchParamVariant) {
        selectedVariantEvent(searchParamVariant)
      }
    }
  }, [searchParamVariant, config.variants, sectionData.allListItems])

  useEffect(() => {
    if (!paramValue) {
      handleChangeSize()
    }
  }, [paramValue])

  const handleCreateList = useCallback(() => {
    setIsCreateList(prev => !prev)
  }, [])

  const handleSectionData = useCallback((key, value) => {
    setSectionData(data => ({ ...data, [key]: value }))
  }, [])

  const selectedVariantEvent = useCallback((value = undefined) => {
    const variant = getVariantById(value || paramValue, config.variants)
    setVariantData(variant ? { ...variant } : undefined)
    const newIsSelected = !!(sectionData.allListItems && sectionData.allListItems[paramValue])
    setIsSelected(newIsSelected)
  }, [paramValue, config.variants, sectionData.allListItems])

  const handleOnLoadAsyncCallback = useCallback(async (shouldRefetch = false) => {
    setIsLoading(true)
    try {
      const listData = await getAllWishlists(client, !shouldRefetch)
      if (shouldRefetch) {
        document.dispatchEvent(new Event('omneo:wishlist:update'))
      }
      handleSectionData('listData', listData)
      const allListItems = flatItemWishList(listData)?.reduce(
        (acc, item) => ({ ...acc, [item.meta.variantId]: 1 }), {}
      ) || {}
      handleSectionData('allListItems', allListItems)
      selectedVariantEvent()
    } catch (error) {
      console.error('Error loading wishlists:', error)
    } finally {
      setIsLoading(false)
    }
  }, [client, handleSectionData, selectedVariantEvent])

  const handleIconClick = useCallback(async () => {
    if (isLoading) return
    if (isSelected) {
      setIsLoading(true)
      const originalAllListItems = sectionData.allListItems
      const originalIsSelected = isSelected
      setIsSelected(false)
      handleSectionData('allListItems', {
        ...sectionData.allListItems,
        [paramValue]: 0
      })

      try {
        await removeVariantFromAllWishlists(client, sectionData.listData, paramValue)
        await handleOnLoadAsyncCallback(true)
      } catch (error) {
        console.error('Error removing from wishlist: ', error)
        setIsSelected(originalIsSelected)
        handleSectionData('allListItems', originalAllListItems)
      } finally {
        setIsLoading(false)
        handleToggle()
      }
    } else {
      handleToggle()
      handleOnLoadAsyncCallback()
    }
  }, [
    isLoading,
    isSelected,
    paramValue,
    client,
    sectionData.allListItems,
    sectionData.listData,
    handleOnLoadAsyncCallback,
    handleSectionData,
    handleToggle
  ])

  const handleChange = useCallback(() => {
    const data = new FormData(formRef.current)
    let hasChanges = false

    data.forEach((value, key) => {
      if (key.startsWith('wishlist_') && value === 'true') {
        hasChanges = true
      }
    })

    setHasChange(hasChanges)
  }, [formRef])

  const handleSubmit = useWishlistFormSubmit({
    client,
    config,
    isCreateList,
    setIsCreateList,
    paramValue,
    variantData,
    sectionData,
    setIsSelected,
    handleSectionData,
    handleOnLoadAsyncCallback,
    setIsLoading
  })

  const handleFormSubmit = async (e) => {
    try {
      await handleSubmit(e)
      handleToggle()
    } catch (error) {
      console.error('Error submitting wishlist form:', error)
    }
  }

  const handleChangeSize = () => {
    setShouldShowProductSelector(true)
    setSizeButtonState('select')
  }
  const handleSelectSize = () => {
    setShouldShowProductSelector(false)
    setSizeButtonState('change')
  }

  return (
    <PageModal
      props={{
        isActive,
        handleToggle,
        modalTitle: accountTranslations.wishlist.add_to_wishlist,
        modal: (
          <WishlistModal
            formRef={formRef}
            isCreateList={isCreateList}
            handleCreateList={handleCreateList}
            isLoading={isLoading}
            onSubmit={handleFormSubmit}
            hasChange={hasChange}
            handleChangeSize={handleChangeSize}
            handleSelectSize={handleSelectSize}
            sizeButtonState={sizeButtonState}
            paramValue={paramValue}
          >
            {isLoading ? (
              <div class="flex flex-col flex-1 gap-4 p-6 overflow-y-auto">
                {[...Array(4)].map((_, index) => (
                  <div class="flex flex-col gap-2 border-b border-neutrals-light-grey pb-6 ">
                    <div class="flex flex-row items-center gap-2">
                      <div class="flex h-6 w-6 border ">
                        <button type="button" class="flex items-center justify-center w-full h-full m-auto"></button>
                      </div>
                      <div class="flex items-center w-full gap-2 bg-neutrals-image-grey">
                        <div class="invisible">Loading</div>
                      </div>
                    </div>
                    <div class="flex flex-row gap-2">
                      {[...Array(4)].map((_, index) => (
                        <div class="relative flex-1 overflow-hidden rounded aspect-square bg-neutrals-image-grey">
                          <div class="w-full h-full bg-neutrals-image-grey"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <x-product-selector
                  class={`breathe block min-h-10 w-full scroll-m-80 ${!isCreateList && shouldShowProductSelector ? '' : 'hidden'}`}
                  data-type="UK"
                  data-product={config.product}
                  data-class="xx"
                  data-options={JSON.stringify(config.variants)}
                  ></x-product-selector>
                  {!shouldShowProductSelector && (
                    <>
                      {sectionData?.listData?.map((list) => (
                        <WishListRow
                          key={list.id}
                          list={list}
                          variantId={paramValue}
                          isLoading={isLoading}
                          isCreateList={isCreateList}
                          handleChange={handleChange}
                        />
                      ))}
                  </>
                  )}
              </>
            )}

            {isCreateList &&
              <NewWishlist
                handleCreateList={handleCreateList}
                isLoading={isLoading}
              />
            }
          </WishlistModal>
        )
      }}
    >
      <button
        type="button"
        aria-label={accountTranslations.wishlist.add_to_wishlist}
        onClick={handleIconClick}
        disabled={isLoading}
        className={'wkh-button wk-headless-wishlist-button rounded-full p-7'}
      >
        {isLoading ? <LoaderSpin wrapperClass="m-1" /> : <WishlistIcon />}
      </button>
    </PageModal>
  )
}

/**
 * The AccountWishlistPDP component
 * @param {OmneoWishlistProps} props - Component props
 */
export const AccountWishlistPDP = (props) => {
  const { client } = useOmneoClient()
  /** @type {WishlistConfig} */
  const config = useMemo(() => ({
    variants: parseJSONProps(props.dataVariants),
    product: props.dataProduct,
    selectedVariant: props.dataSelectedVariant
  }), [props.dataVariants, props.dataProduct, props.dataSelectedVariant])

  if (!client) return null
  return <AccountWishListPDPBase client={client} config={config} />
}
